import { Box, Button, HStack, Spinner, Text, useToast } from '@chakra-ui/react';
import {
  cancelFinancialReportRecapPreviewFromAPI,
  getFinancialReportRecapPreviewResultFromAPI,
} from '@libraries/api/financial-report/financial-report.api';
import {
  IFinancialReportPreviewAsync,
  IFinancialReportUploadPreview,
} from '@libraries/api/financial-report/financial-report.types';
import { useRouter } from 'next/router';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type RecapPreviewState = {
  previewId: string;
  recapId: number;
  recapName: string;
  status: 'processing' | 'done' | 'failed' | 'cancelled';
  progress: number;
  phase: string;
  result?: IFinancialReportUploadPreview | null;
};

export type PreviewFiles = {
  file_pesanan: File | null;
  file_pendapatan: File | null;
};

interface IRecapPreviewContext {
  preview: RecapPreviewState | null;
  // sinyal minta buka modal preview untuk recap tertentu (dikonsumsi halaman detail)
  openForRecapId: number | null;
  requestOpenModal: (recapId: number) => void;
  consumeOpenRequest: () => void;
  trackPreview: (p: {
    previewId: string;
    recapId: number;
    recapName: string;
    files: PreviewFiles;
  }) => void;
  // file upload disimpan di provider (bertahan walau pindah menu; komponen detail unmount)
  getPreviewFiles: () => PreviewFiles | null;
  cancelPreview: () => void;
  clearPreview: () => void;
}

const RecapPreviewContext = createContext<IRecapPreviewContext>({
  preview: null,
  openForRecapId: null,
  requestOpenModal: () => undefined,
  consumeOpenRequest: () => undefined,
  trackPreview: () => undefined,
  getPreviewFiles: () => null,
  cancelPreview: () => undefined,
  clearPreview: () => undefined,
});

export const useRecapPreview = () => useContext(RecapPreviewContext);

const STORAGE_KEY = 'recap_preview_active';
const DETAIL_PATH = '/others/report-finance-detail/';

export const RecapPreviewProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const toast = useToast();
  const [preview, setPreview] = useState<RecapPreviewState | null>(null);
  const [openForRecapId, setOpenForRecapId] = useState<number | null>(null);
  const pollingRef = useRef(false);
  // File tak bisa di-serialize ke localStorage; simpan di ref (bertahan selama SPA
  // hidup / pindah menu, hilang saat reload penuh — itu batasan wajar untuk File).
  const filesRef = useRef<PreviewFiles | null>(null);

  // hydrate dari localStorage (bertahan walau reload penuh). Setelah reload, File
  // di FE hilang, tapi file disimpan di SERVER (by preview_id) → confirm tetap bisa.
  // Sinkronkan ke server sekali: kalau sudah done, ambil result-nya lagi.
  useEffect(() => {
    let parsed: RecapPreviewState | null = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) parsed = JSON.parse(raw) as RecapPreviewState;
    } catch {
      /* ignore */
    }
    if (!parsed?.previewId) return;
    setPreview(parsed);
    const previewId = parsed.previewId;
    getFinancialReportRecapPreviewResultFromAPI(previewId)
      .then((res) => {
        const data = res?.data?.data as IFinancialReportPreviewAsync;
        if (data?.status === 'done') {
          setPreview((prev) =>
            prev && prev.previewId === previewId
              ? {
                  ...prev,
                  status: 'done',
                  progress: 100,
                  result: data.result || {},
                }
              : prev,
          );
        } else if (data?.status === 'failed' || data?.status === 'cancelled') {
          setPreview((prev) =>
            prev && prev.previewId === previewId ? null : prev,
          );
        }
        // processing → poll effect yang handle
      })
      .catch(() => undefined);
  }, []);

  // simpan saat processing/done (biar preview_id bertahan kalau reload → confirm
  // masih bisa). result tidak disimpan (bisa besar; di-refetch saat hydrate).
  useEffect(() => {
    try {
      if (
        preview &&
        (preview.status === 'processing' || preview.status === 'done')
      ) {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...preview, result: undefined }),
        );
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [preview]);

  // polling global: jalan selama status processing, lepas dari halaman mana pun
  useEffect(() => {
    if (!preview || preview.status !== 'processing' || pollingRef.current) {
      return undefined;
    }
    pollingRef.current = true;
    const { previewId, recapName } = preview;
    let stopped = false;

    (async () => {
      while (!stopped) {
        try {
          const res =
            await getFinancialReportRecapPreviewResultFromAPI(previewId);
          const data = res?.data?.data as IFinancialReportPreviewAsync;
          if (data?.status === 'done') {
            setPreview((prev) =>
              prev && prev.previewId === previewId
                ? {
                    ...prev,
                    status: 'done',
                    progress: 100,
                    result: data.result || {},
                  }
                : prev,
            );
            toast({
              title: 'Pratinjau siap',
              description: `Recap "${recapName}" siap ditinjau.`,
              status: 'success',
              duration: 6000,
              isClosable: true,
            });
            break;
          }
          if (data?.status === 'failed') {
            toast({
              title: 'Pratinjau gagal diproses',
              description: data.error || `Recap "${recapName}"`,
              status: 'error',
              isClosable: true,
            });
            setPreview((prev) =>
              prev && prev.previewId === previewId ? null : prev,
            );
            break;
          }
          if (data?.status === 'cancelled') {
            setPreview((prev) =>
              prev && prev.previewId === previewId ? null : prev,
            );
            break;
          }
          setPreview((prev) =>
            prev && prev.previewId === previewId
              ? {
                  ...prev,
                  progress: Math.min(
                    100,
                    Math.max(0, data?.progress ?? prev.progress),
                  ),
                  phase: data?.phase || prev.phase,
                }
              : prev,
          );
        } catch {
          /* transient, coba lagi */
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      pollingRef.current = false;
    })();

    return () => {
      stopped = true;
      pollingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.previewId, preview?.status]);

  const trackPreview = useCallback(
    (p: {
      previewId: string;
      recapId: number;
      recapName: string;
      files: PreviewFiles;
    }) => {
      filesRef.current = p.files;
      setPreview({
        previewId: p.previewId,
        recapId: p.recapId,
        recapName: p.recapName,
        status: 'processing',
        progress: 0,
        phase: 'Memproses file',
      });
    },
    [],
  );

  const getPreviewFiles = useCallback(() => filesRef.current, []);

  const cancelPreview = useCallback(() => {
    filesRef.current = null;
    setPreview((prev) => {
      if (prev?.previewId) {
        cancelFinancialReportRecapPreviewFromAPI(prev.previewId).catch(
          () => undefined,
        );
      }
      return null;
    });
    toast({ title: 'Pratinjau dibatalkan', status: 'info' });
  }, [toast]);

  const clearPreview = useCallback(() => {
    filesRef.current = null;
    setPreview(null);
  }, []);

  // minta halaman detail buka modal preview; kalau belum di halaman itu, arahkan dulu
  const requestOpenModal = useCallback(
    (recapId: number) => {
      setOpenForRecapId(recapId);
      if (!router.asPath.includes(DETAIL_PATH + recapId)) {
        router.push(DETAIL_PATH + recapId);
      }
    },
    [router],
  );
  const consumeOpenRequest = useCallback(() => setOpenForRecapId(null), []);

  // indikator mengambang: tampil di SEMUA halaman selama processing/done
  const showPill =
    !!preview && (preview.status === 'processing' || preview.status === 'done');
  const isProcessing = preview?.status === 'processing';

  return (
    <RecapPreviewContext.Provider
      value={{
        preview,
        openForRecapId,
        requestOpenModal,
        consumeOpenRequest,
        trackPreview,
        getPreviewFiles,
        cancelPreview,
        clearPreview,
      }}
    >
      {children}
      {showPill && preview && (
        <Box
          position="fixed"
          bottom="6"
          right="6"
          zIndex={1500}
          bg={isProcessing ? 'blue.500' : 'green.500'}
          color="white"
          px="4"
          py="3"
          borderRadius="lg"
          boxShadow="lg"
          cursor="pointer"
          maxW="360px"
          onClick={() => requestOpenModal(preview.recapId)}
          _hover={{ opacity: 0.92 }}
        >
          <HStack spacing="3">
            {isProcessing && <Spinner size="sm" thickness="2px" />}
            <Box minW="0">
              <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                {isProcessing
                  ? `Memproses pratinjau ${preview.progress}%`
                  : `Pratinjau "${preview.recapName}" siap`}
              </Text>
              <Text fontSize="xs" opacity={0.9} noOfLines={1}>
                {isProcessing
                  ? `${preview.phase} — ${preview.recapName}`
                  : 'Klik untuk buka & tinjau'}
              </Text>
            </Box>
            <Button
              size="xs"
              colorScheme="whiteAlpha"
              onClick={(e) => {
                e.stopPropagation();
                if (isProcessing) cancelPreview();
                else clearPreview();
              }}
            >
              {isProcessing ? 'Batal' : 'Tutup'}
            </Button>
          </HStack>
        </Box>
      )}
    </RecapPreviewContext.Provider>
  );
};
